export function cursorTrail(props) {
  const { ref } = props;
  const ctx = ref.current?.getContext("2d");

  // Configuration
  const config = {
    particleCount: 10,
    particleLifespan: 25,
    particleSize: { min: 1, max: 5 },
    particleSpeed: { min: 0.3, max: 1.5 },
    glowIntensity: 15,
    hueRange: { start: 25, end: 45 }, // Orange range
    saturation: 100,
    lightness: 60,
    mouseSensitivity: 0.25,
    trailLength: 10,
    frameInterval: 1000 / 60, // Target 60fps
  };

  // State
  let lastPosition = { x: 0, y: 0 };
  let currentPosition = { x: 0, y: 0 };
  let particles = [];
  let trailPoints = [];
  let running = true;
  let lastFrameTime = 0;
  let lastMoveTime = 0;
  let isModalOpen = false;

  // Particle class
  class Particle {
    constructor(x, y, size, hue) {
      this.x = x;
      this.y = y;
      this.size = size;
      this.alpha = 1;
      this.hue = hue;
      this.decay = 0.96 + Math.random() * 0.03;
      this.life = config.particleLifespan;
      this.maxSize = size;

      // Create more directed particles that follow the cursor movement
      const angle = Math.random() * Math.PI * 2;
      const speed =
        config.particleSpeed.min +
        Math.random() * (config.particleSpeed.max - config.particleSpeed.min);
      this.vx = Math.cos(angle) * speed;
      this.vy = Math.sin(angle) * speed;

      // Add slight gravity effect
      this.gravity = 0.01 + Math.random() * 0.02;

      // Random shape variation (0: circle, 1: square, 2: diamond)
      this.shape = Math.floor(Math.random() * 3);
    }

    update() {
      this.life--;
      this.alpha *= this.decay;
      this.x += this.vx;
      this.y += this.vy + this.gravity;

      // Pulsating size effect
      const lifeRatio = this.life / config.particleLifespan;
      const pulseSpeed = 0.1;
      this.size =
        this.maxSize *
        (0.7 + 0.3 * Math.sin(lifeRatio * Math.PI * 2 * pulseSpeed));

      return this.life > 0 && this.alpha > 0.01;
    }

    draw() {
      if (!ctx) return;

      // Enhanced glow effect
      ctx.shadowBlur = config.glowIntensity * this.alpha;
      ctx.shadowColor = `hsla(${this.hue}, ${config.saturation}%, ${config.lightness}%, ${this.alpha})`;

      // Draw particle with different shapes for variety
      ctx.fillStyle = `hsla(${this.hue}, ${config.saturation}%, ${config.lightness}%, ${this.alpha})`;

      switch (this.shape) {
        case 0: // Circle
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          ctx.fill();
          break;
        case 1: // Square
          ctx.save();
          ctx.translate(this.x, this.y);
          ctx.rotate(this.life * 0.05);
          ctx.fillRect(-this.size / 2, -this.size / 2, this.size, this.size);
          ctx.restore();
          break;
        case 2: // Diamond
          ctx.save();
          ctx.translate(this.x, this.y);
          ctx.rotate(this.life * 0.03);
          ctx.beginPath();
          ctx.moveTo(0, -this.size);
          ctx.lineTo(this.size, 0);
          ctx.lineTo(0, this.size);
          ctx.lineTo(-this.size, 0);
          ctx.closePath();
          ctx.fill();
          ctx.restore();
          break;
      }
    }
  }

  // Add particles at current position
  function addParticles() {
    const hueRange = config.hueRange.end - config.hueRange.start;

    for (let i = 0; i < config.particleCount / 3; i++) {
      const size =
        config.particleSize.min +
        Math.random() * (config.particleSize.max - config.particleSize.min);
      const hue = config.hueRange.start + Math.random() * hueRange;
      particles.push(
        new Particle(currentPosition.x, currentPosition.y, size, hue)
      );
    }
  }

  // Update trail points
  function updateTrail() {
    // Add current position to trail
    if (
      trailPoints.length === 0 ||
      Math.hypot(
        currentPosition.x - trailPoints[0].x,
        currentPosition.y - trailPoints[0].y
      ) > 5
    ) {
      trailPoints.unshift({ x: currentPosition.x, y: currentPosition.y });

      // Limit trail length
      if (trailPoints.length > config.trailLength) {
        trailPoints.pop();
      }
    }
  }

  // Draw the main trail
  function drawTrail() {
    if (!ctx || trailPoints.length < 2) return;

    // Draw the main trail with dual-line effect for more professional look
    const primaryLineWidth = 3;
    const secondaryLineWidth = 6;

    // Draw secondary glow trail (wider, more transparent)
    ctx.lineWidth = secondaryLineWidth;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    // Create gradient for the secondary trail (wider glow effect)
    const secondaryGradient = ctx.createLinearGradient(
      trailPoints[0].x,
      trailPoints[0].y,
      trailPoints[trailPoints.length - 1].x,
      trailPoints[trailPoints.length - 1].y
    );

    secondaryGradient.addColorStop(
      0,
      `hsla(${config.hueRange.start}, ${config.saturation}%, ${config.lightness}%, 0.3)`
    );
    secondaryGradient.addColorStop(
      1,
      `hsla(${config.hueRange.end}, ${config.saturation}%, ${config.lightness}%, 0)`
    );

    ctx.strokeStyle = secondaryGradient;
    ctx.shadowBlur = config.glowIntensity * 1.5;
    ctx.shadowColor = `hsla(${config.hueRange.start}, ${config.saturation}%, ${config.lightness}%, 0.3)`;

    // Draw the secondary path
    ctx.beginPath();
    ctx.moveTo(trailPoints[0].x, trailPoints[0].y);

    // Use bezier curves for even smoother trail
    if (trailPoints.length > 2) {
      for (let i = 0; i < trailPoints.length - 2; i++) {
        const xc1 = (trailPoints[i].x + trailPoints[i + 1].x) / 2;
        const yc1 = (trailPoints[i].y + trailPoints[i + 1].y) / 2;
        const xc2 = (trailPoints[i + 1].x + trailPoints[i + 2].x) / 2;
        const yc2 = (trailPoints[i + 1].y + trailPoints[i + 2].y) / 2;

        ctx.bezierCurveTo(
          trailPoints[i + 1].x,
          trailPoints[i + 1].y,
          xc1,
          yc1,
          xc2,
          yc2
        );
      }
    } else {
      // Fallback to quadratic curves for shorter trails
      for (let i = 0; i < trailPoints.length - 1; i++) {
        const xc = (trailPoints[i].x + trailPoints[i + 1].x) / 2;
        const yc = (trailPoints[i].y + trailPoints[i + 1].y) / 2;
        ctx.quadraticCurveTo(trailPoints[i].x, trailPoints[i].y, xc, yc);
      }
    }

    // Connect to the last point
    if (trailPoints.length > 1) {
      const last = trailPoints[trailPoints.length - 1];
      ctx.lineTo(last.x, last.y);
    }

    ctx.stroke();

    // Draw primary trail (thinner, more vibrant)
    ctx.lineWidth = primaryLineWidth;

    // Create gradient for the primary trail
    const primaryGradient = ctx.createLinearGradient(
      trailPoints[0].x,
      trailPoints[0].y,
      trailPoints[trailPoints.length - 1].x,
      trailPoints[trailPoints.length - 1].y
    );

    primaryGradient.addColorStop(
      0,
      `hsla(${config.hueRange.start}, ${config.saturation}%, ${config.lightness}%, 0.9)`
    );
    primaryGradient.addColorStop(
      1,
      `hsla(${config.hueRange.end}, ${config.saturation}%, ${config.lightness}%, 0.1)`
    );

    ctx.strokeStyle = primaryGradient;
    ctx.shadowBlur = config.glowIntensity;
    ctx.shadowColor = `hsla(${config.hueRange.start}, ${config.saturation}%, ${config.lightness}%, 0.7)`;

    // Draw the primary path
    ctx.beginPath();
    ctx.moveTo(trailPoints[0].x, trailPoints[0].y);

    // Use the same curve algorithm as above
    if (trailPoints.length > 2) {
      for (let i = 0; i < trailPoints.length - 2; i++) {
        const xc1 = (trailPoints[i].x + trailPoints[i + 1].x) / 2;
        const yc1 = (trailPoints[i].y + trailPoints[i + 1].y) / 2;
        const xc2 = (trailPoints[i + 1].x + trailPoints[i + 2].x) / 2;
        const yc2 = (trailPoints[i + 1].y + trailPoints[i + 2].y) / 2;

        ctx.bezierCurveTo(
          trailPoints[i + 1].x,
          trailPoints[i + 1].y,
          xc1,
          yc1,
          xc2,
          yc2
        );
      }
    } else {
      for (let i = 0; i < trailPoints.length - 1; i++) {
        const xc = (trailPoints[i].x + trailPoints[i + 1].x) / 2;
        const yc = (trailPoints[i].y + trailPoints[i + 1].y) / 2;
        ctx.quadraticCurveTo(trailPoints[i].x, trailPoints[i].y, xc, yc);
      }
    }

    if (trailPoints.length > 1) {
      const last = trailPoints[trailPoints.length - 1];
      ctx.lineTo(last.x, last.y);
    }

    ctx.stroke();
  }

  // Animation loop
  function renderAnimation(timestamp) {
    if (!running) return;

    // Throttle frame rate
    if (timestamp - lastFrameTime < config.frameInterval) {
      window.requestAnimationFrame(renderAnimation);
      return;
    }

    lastFrameTime = timestamp;

    if (!ctx || !ctx.canvas) return;

    // Clear canvas
    ctx.globalCompositeOperation = "source-over";
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    // Smooth cursor position
    currentPosition.x +=
      (lastPosition.x - currentPosition.x) * config.mouseSensitivity;
    currentPosition.y +=
      (lastPosition.y - currentPosition.y) * config.mouseSensitivity;

    // Update and add trail points
    updateTrail();

    // Draw the main trail
    ctx.globalCompositeOperation = "lighter";
    drawTrail();

    // Add new particles occasionally
    if (Math.random() < 0.3 && trailPoints.length > 0) {
      addParticles();
    }

    // Update and draw particles
    particles = particles.filter((particle) => {
      const isAlive = particle.update();
      if (isAlive) {
        particle.draw();
      }
      return isAlive;
    });

    // Reset shadow for next frame
    ctx.shadowBlur = 0;

    // Continue animation loop
    window.requestAnimationFrame(renderAnimation);
  }

  // Handle mouse movement
  function move(event) {
    // Throttle mouse move events
    const now = Date.now();
    if (now - lastMoveTime < 10) {
      // 10ms throttle
      return;
    }
    lastMoveTime = now;

    if (event instanceof MouseEvent) {
      lastPosition.x = event.clientX;
      lastPosition.y = event.clientY;
    } else if (event.touches && event.touches.length > 0) {
      lastPosition.x = event.touches[0].pageX;
      lastPosition.y = event.touches[0].pageY;
      event.preventDefault();
    }

    // Check if cursor is over a modal
    const elementUnderCursor = document.elementFromPoint(
      lastPosition.x,
      lastPosition.y
    );
    if (elementUnderCursor) {
      const isModal = elementUnderCursor.closest(
        '[class*="modal"], [class*="Modal"]'
      );
      isModalOpen = !!isModal;

      // Adjust visibility based on modal presence
      if (ref.current) {
        ref.current.style.opacity = isModalOpen ? "0.3" : "1";
        ref.current.style.zIndex = isModalOpen ? "1" : "10000";
      }
    }
  }

  // Initialize on first mouse move
  function onMouseMove(e) {
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("touchstart", onMouseMove);

    document.addEventListener("mousemove", move, { passive: true });
    document.addEventListener("touchmove", move, { passive: false });
    document.addEventListener("touchstart", move, { passive: false });

    move(e);

    // Initialize current position to prevent jumps
    currentPosition.x = lastPosition.x;
    currentPosition.y = lastPosition.y;

    // Start animation loop
    lastFrameTime = 0;
    window.requestAnimationFrame(renderAnimation);
  }

  // Handle canvas resize
  function resizeCanvas() {
    if (!ctx || !ctx.canvas) return;

    ctx.canvas.width = window.innerWidth;
    ctx.canvas.height = window.innerHeight;
  }

  // Stop animation
  function stopAnimation() {
    running = false;
  }

  // Start animation
  function startAnimation() {
    if (!running) {
      running = true;
      lastFrameTime = 0;
      window.requestAnimationFrame(renderAnimation);
    }
  }

  // Check for modals
  function checkForModalChanges() {
    const modalElements = document.querySelectorAll(
      '[class*="modal"], [class*="Modal"]'
    );
    isModalOpen = modalElements.length > 0;

    if (ref.current) {
      ref.current.style.opacity = isModalOpen ? "0.3" : "1";
      ref.current.style.zIndex = isModalOpen ? "1" : "10000";
    }
  }

  // Initialize cursor trail
  function renderTrailCursor() {
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("touchstart", onMouseMove);
    window.addEventListener("orientationchange", resizeCanvas);
    window.addEventListener("resize", resizeCanvas);
    window.addEventListener("focus", startAnimation);
    window.addEventListener("blur", stopAnimation);

    // Set up mutation observer to detect modal changes
    const observer = new MutationObserver(checkForModalChanges);
    observer.observe(document.body, { childList: true, subtree: true });

    // Initial setup
    setTimeout(checkForModalChanges, 100);
    resizeCanvas();
  }

  // Clean up event listeners
  function cleanUp() {
    document.removeEventListener("mousemove", move);
    document.removeEventListener("touchmove", move);
    document.removeEventListener("touchstart", move);
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("touchstart", onMouseMove);
    window.removeEventListener("orientationchange", resizeCanvas);
    window.removeEventListener("resize", resizeCanvas);
    window.removeEventListener("focus", startAnimation);
    window.removeEventListener("blur", stopAnimation);
  }

  // Return public methods
  return { cleanUp, renderTrailCursor, stopAnimation, startAnimation };
}
